import { Label } from '@/components/shared/Label/Label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { TabsContent } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { getIconMethodPaymentSimple } from '@/utils/getIconMethodPayment'
import { Check, Info } from 'lucide-react'
import React from 'react'
import { UseFormClearErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'

type PaymentMethod = {
  type: string
  name?: string
}

type PaymentTabProps = {
  setValue: UseFormSetValue<CustomizeCheckoutData>
  watch: UseFormWatch<CustomizeCheckoutData>
  clearErrors: UseFormClearErrors<CustomizeCheckoutData>
  paymentMethods?: PaymentMethod[]
  availablePaymentMethods?: PaymentMethod[]
}

export function PaymentTab({
  setValue,
  watch,
  clearErrors,
  paymentMethods,
  availablePaymentMethods,
}: PaymentTabProps) {
  const { t } = useTranslation(['common', 'checkouts'])
  const methods = paymentMethods?.length
    ? paymentMethods
    : availablePaymentMethods || []

  return (
    <TabsContent value="payment" className="mt-8">
      <section className="w-full">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <Label className="text-lg font-semibold">
                {t('default_payment_method')}
              </Label>
              <p className="text-muted-foreground text-sm mt-1.5 max-w-xl">
                {t('default_payment_method_description')}
              </p>
            </div>
            {methods.length > 0 && (
              <span className="text-xs text-zinc-500 bg-zinc-800/60 px-3 py-1.5 rounded-full border border-zinc-700/40 whitespace-nowrap w-fit">
                {methods.length} {t('common:configured')}
              </span>
            )}
          </div>
        </div>

        {(!paymentMethods || paymentMethods.length === 0) && (
          <div className="flex items-start gap-3 rounded-xl border border-zinc-700/40 bg-zinc-900/50 p-5 mb-8">
            <Info size={18} className="text-zinc-400 shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-400 leading-relaxed">
              {t('common:no_payment_methods_configured')}
            </p>
          </div>
        )}

        <RadioGroup
          value={watch('default_payment_method')}
          onValueChange={(value) => {
            setValue('default_payment_method', value)
            clearErrors('default_payment_method')
          }}
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 xl:grid-cols-2 2xl:grid-cols-3"
        >
          {methods.map((method) => {
            const isSelected = watch('default_payment_method') === method.type
            const icon = getIconMethodPaymentSimple(method.type)

            return (
              <div key={method.type} className="relative">
                <RadioGroupItem
                  value={method.type}
                  id={`payment-${method.type}`}
                  className="sr-only"
                />
                <Label
                  htmlFor={`payment-${method.type}`}
                  data-cy={`card-payment-${method.type}`}
                  className={cn(
                    'border rounded-xl p-4 sm:p-5 flex flex-col items-center justify-center gap-2.5 sm:gap-3 cursor-pointer transition-all duration-200 hover:border-primary/50 hover:bg-zinc-800/30 min-h-[110px] sm:min-h-[130px] group',
                    isSelected
                      ? 'border-primary ring-2 ring-primary/20 bg-gradient-to-b from-primary/8 to-primary/3'
                      : 'border-zinc-700/60 bg-zinc-900/80',
                  )}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <Check
                        size={11}
                        strokeWidth={3}
                        className="text-black"
                      />
                    </div>
                  )}

                  <div
                    className={cn(
                      'w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200',
                      isSelected
                        ? 'bg-primary/10 text-primary scale-105'
                        : 'bg-zinc-800/60 text-zinc-400 group-hover:bg-zinc-800/80',
                    )}
                  >
                    {icon &&
                      React.cloneElement(icon, {
                        width: 32,
                        height: 32,
                        className: cn(
                          'transition-colors duration-200 sm:w-9 sm:h-9',
                          isSelected ? 'text-primary' : 'text-zinc-400',
                        ),
                      })}
                  </div>

                  <span
                    className={cn(
                      'text-[11px] sm:text-xs font-medium capitalize text-center leading-tight transition-colors duration-200 line-clamp-2',
                      isSelected
                        ? 'text-primary'
                        : 'text-zinc-400 group-hover:text-zinc-300',
                    )}
                  >
                    {method.name || t(method.type)}
                  </span>
                </Label>
              </div>
            )
          })}
        </RadioGroup>
      </section>
    </TabsContent>
  )
}
